import React, { SyntheticEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isSameDay } from 'date-fns';

import { TertiaryButtonBlack } from '@common/button';
import { Heading4 } from '@common/Typography';
import { DateStyle, formatToLocaleDateString } from '@utils/date';
import { handleKeystroke } from '@utils/handleKeystroke';

interface Props {
  disableNext: boolean;
  disablePrevious: boolean;
  endDate: Date;
  onCancelClick: (event: SyntheticEvent<HTMLButtonElement>) => void;
  onNextClick: (event: SyntheticEvent<HTMLButtonElement>) => void;
  onPreviousClick: (event: SyntheticEvent<HTMLButtonElement>) => void;
  isSearching?: boolean;
  startDate: Date;
  timezone?: string | null;
}

function TimeTableHeader(props: Props) {
  const {
    disableNext,
    disablePrevious,
    endDate,
    isSearching,
    onCancelClick,
    onNextClick,
    onPreviousClick,
    startDate,
    timezone,
  } = props;
  const { i18n, t } = useTranslation();

  const title = useMemo(() => {
    const start = formatToLocaleDateString(startDate, i18n.language, {
      day: DateStyle.NUMERIC,
      month: DateStyle.LONG,
      timezone: timezone ?? undefined,
    });
    const end = formatToLocaleDateString(endDate, i18n.language, {
      day: DateStyle.NUMERIC,
      month: DateStyle.LONG,
      year: DateStyle.NUMERIC,
      timezone: timezone ?? undefined,
    });

    if (isSameDay(startDate, endDate)) {
      return end;
    }

    return `${start} - ${end}
    `;
  }, [startDate, endDate, i18n.language, timezone]);

  return (
    <div className="flex flex-col">
      <div className="sm:space-between flex flex-col items-center justify-center p-4 border border-gray-300 space-y-2 sm:flex-row sm:space-y-0">
        <div className="flex-1">
          <Heading4>{title}</Heading4>
        </div>
        <div className="flex space-x-4">
          {isSearching && (
            <TertiaryButtonBlack onClick={onCancelClick} type="button">
              {t('button.cancel')}
            </TertiaryButtonBlack>
          )}
          <TertiaryButtonBlack
            disabled={disablePrevious || isSearching}
            icon={['fas', 'chevron-left']}
            onClick={onPreviousClick}
            onKeyDown={(e) => handleKeystroke(e.code)}
            type="button"
          >
            {t('button.previous')}
          </TertiaryButtonBlack>
          <TertiaryButtonBlack
            disabled={disableNext || isSearching}
            onClick={onNextClick}
            onKeyDown={(e) => handleKeystroke(e.code)}
            rightIcon={['fas', 'chevron-right']}
            type="button"
          >
            {t('button.next')}
          </TertiaryButtonBlack>
        </div>
      </div>
    </div>
  );
}

export default TimeTableHeader;
